import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/seo/Seo";

const PRINCIPLES = [
  { n: "01", t: "Wellness is not a luxury.", b: "It is the everyday practice of paying attention. The price of admission is time, not money.", tint: "bg-cream" },
  { n: "02", t: "We do not innovate. We remember.", b: "The practices on this site predate us by centuries — Ayurvedic, Japanese, Mediterranean. Our work is curation and translation, not invention.", tint: "bg-clay" },
  { n: "03", t: "Less is the discipline.", b: "Subtract before adding. One ritual held for a year will alter a life more than ten begun in January.", tint: "bg-terracotta/15" },
  { n: "04", t: "The body is a partner.", b: "Movement, breath, bathing — these are conversations, not optimisations. We listen first.", tint: "bg-forest/10" },
  { n: "05", t: "Evidence is welcome, slowness is honoured.", b: "Where science clarifies an old practice, we welcome it. Where it cannot, we are patient. We do not need everything proven to keep it.", tint: "bg-cream" },
];

const SHELF = "/generated/an-open-shelf-in-warm-morning-light-thr-df4a2c.png";
const BATH = "/generated/a-warm-bath-scene-from-above-a-deep-sto-bd87e5.png";
const OLIVE = "/generated/a-low-angle-still-life-on-a-deep-forest-e6ab9b.png";
const WIDE = "/generated/a-wide-cinematic-overhead-shot-of-a-long-97b269.png";

export default function Philosophy() {
  return (
    <div data-testid="philosophy-page">
      <Seo
        path="/philosophy"
        title="The Philosophy"
        description="Wellness is not a luxury. It is a way of paying attention. Five quiet principles that shape everything Stillform publishes and makes."
      />

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 px-6 md:px-12 lg:px-16 bg-bone">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="overline mb-6 text-terracotta">The Philosophy</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[8rem] font-light leading-[0.92] tracking-[-0.03em] text-ink">
              Wellness is not<br />
              <em className="italic text-terracotta">a luxury.</em>
            </h1>
          </div>
          <p className="lg:col-span-4 font-display italic text-xl md:text-2xl text-charcoal leading-snug">
            It is a way of<br />paying attention.
          </p>
        </div>
      </section>

      {/* FULL-BLEED WIDE BAND */}
      <section className="relative overflow-hidden bg-ink">
        <div className="aspect-[16/8] md:aspect-[16/6]">
          <img src={WIDE} alt="The table of ritual, slowly laid" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* FOUNDER NOTE — image left, long-form text right */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-cream">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <div className="img-zoom aspect-[4/5] overflow-hidden mb-6">
              <img
                src={SHELF}
                alt="Apothecary shelf, warm morning light"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="overline text-terracotta">Founder, Stillform</p>
            <p className="font-display italic text-2xl md:text-3xl text-ink leading-snug mt-3">
              "Build the rest first.<br />
              <span className="text-forest">Then build the day around it.</span>"
            </p>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 space-y-7 text-base md:text-lg text-ink font-light leading-[1.85]">
            <p className="overline text-terracotta">A letter · 2026</p>
            <p className="drop-cap font-display italic text-2xl md:text-3xl text-ink leading-snug">
              Stillform began through a growing curiosity around wellness,
              slower living, and the rituals that quietly shape how we feel.
            </p>
            <p>
              My interest in wellbeing started in my twenties, but it became
              deeply personal over the last two years, following a period of
              personal loss that affected both my mental and physical health.
              Sleep became difficult. Anxiety became constant. My body no
              longer felt like a place of balance.
            </p>
            <p>
              What began as a personal search for recovery slowly became an
              exploration into nervous system health, longevity, recovery,
              movement, and the relationship between ancient practices and
              modern science.
            </p>
            <p>
              I spent time reading, researching, experimenting, and rebuilding
              through small daily rituals — better sleep, slower mornings,
              adaptogens, movement, breathwork, nutrition, recovery practices,
              and a more intentional way of living.
            </p>
            <p>
              Over time, my health began to change. My anxiety softened. Sleep
              returned. My energy stabilised. Even my blood markers gradually
              returned to a healthier place.
            </p>
            <p className="font-display italic text-xl md:text-2xl text-terracotta leading-snug py-4 border-y border-terracotta/30">
              Stillform is not built around perfection or optimisation. It is
              an ongoing exploration of what helps us live with greater
              balance, clarity and longevity in a modern world that often
              pulls us away from ourselves.
            </p>
            <p>
              This journal exists to explore wellness through a calmer, more
              thoughtful lens — one rooted in ritual, evidence, sensory
              living, and practices that genuinely support the mind and body
              over time.
            </p>
            <p className="overline text-stone pt-4">— Likhitha Nagaraj · Founder, Stillform</p>
          </div>
        </div>
      </section>

      {/* MID-PAGE PULL — forest green with faded olive still life */}
      <section className="relative overflow-hidden border-t border-forest/40 bg-forest text-cream">
        <div className="absolute inset-0 opacity-25 hidden md:block">
          <img src={OLIVE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest/70" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-16 py-28 md:py-40">
          <div className="max-w-4xl">
            <p className="overline mb-8 text-terracotta-light">Five standing principles</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0] tracking-[-0.02em] text-cream">
              A short vocabulary<br />
              that <em className="italic text-terracotta-light">holds everything.</em>
            </h2>
            <p className="mt-10 font-display italic text-xl md:text-2xl text-cream/85 max-w-2xl leading-snug">
              The ideas below are not rules. They are a way of listening — to
              the body, the day, and what we choose to make.
            </p>
          </div>
        </div>
      </section>

      {/* FIVE PRINCIPLES — color-blocked cards */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-bone border-t border-hairline">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {PRINCIPLES.map((p) => (
            <article key={p.n} className={`${p.tint} p-10 md:p-12`} data-testid={`principle-${p.n}`}>
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-mono text-xs tracking-[0.22em] uppercase text-terracotta">{p.n}</span>
                <span className="font-mono text-xs tracking-[0.22em] uppercase text-stone">
                  Principle
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-ink leading-[1.05] mb-6 max-w-md">
                {p.t}
              </h3>
              <p className="text-base md:text-lg text-charcoal font-light leading-[1.75] max-w-md">
                {p.b}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* FULL-BLEED BATH IMAGE */}
      <section className="relative overflow-hidden bg-ink">
        <div className="aspect-[16/8] md:aspect-[16/6]">
          <img src={BATH} alt="A warm bath, slowly drawn" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16 py-10 md:py-14 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent">
            <p className="overline text-cream/80 mb-3">A standing instruction</p>
            <h2 className="font-display text-2xl md:text-4xl lg:text-5xl text-cream font-light italic leading-tight max-w-3xl">
              Choose a single practice.<br />Keep it for a season.
            </h2>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center bg-cream">
        <p className="overline mb-6 text-terracotta">Begin somewhere</p>
        <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight text-ink mb-10 leading-tight">
          The ritual is already <em className="italic text-forest">here.</em><br />
          You just have to return to it.
        </h2>
        <div className="flex items-center justify-center gap-6 flex-wrap">
          <Link
            to="/rituals"
            data-testid="philosophy-rituals-cta"
            className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-cream bg-forest px-6 py-4 hover:bg-terracotta transition-colors"
          >
            Browse the Rituals <ArrowUpRight size={14} />
          </Link>
          <Link
            to="/journal"
            data-testid="philosophy-journal-cta"
            className="text-[0.72rem] tracking-[0.22em] uppercase font-medium text-terracotta link-underline"
          >
            Read the Journal
          </Link>
        </div>
      </section>
    </div>
  );
}
